import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Tone = 'success' | 'danger' | 'neutral';

const toneMap: Record<Tone, { wrap: string; icon: string; name: string }> = {
  success: {
    wrap: 'border-primary-200 bg-primary-50',
    icon: 'bg-primary-600 text-background-50',
    name: 'ri-shield-check-line',
  },
  danger: {
    wrap: 'border-accent-200 bg-accent-50',
    icon: 'bg-accent-600 text-background-50',
    name: 'ri-error-warning-line',
  },
  neutral: {
    wrap: 'border-background-200 bg-background-100',
    icon: 'bg-foreground-950 text-background-50',
    name: 'ri-information-line',
  },
};

export default function OutcomePanel({
  tone,
  title,
  text,
  detail,
  footer,
}: {
  tone: Tone;
  title: string;
  text: string;
  detail?: string;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const style = toneMap[tone];

  return (
    <div className={`rounded-2xl border p-5 md:p-7 text-center ${style.wrap}`}>
      <span
        className={`mx-auto flex items-center justify-center w-14 h-14 rounded-full ${style.icon}`}
        aria-hidden="true"
      >
        <i className={`${style.name} text-2xl`} />
      </span>
      <h2 className="mt-4 text-[19px] font-semibold text-foreground-950 font-heading">{title}</h2>
      <p className="mx-auto mt-2 max-w-[420px] text-[13px] leading-relaxed text-foreground-600">{text}</p>
      {detail ? (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-3 py-1 text-[11.5px] font-semibold text-foreground-700 font-label">
          <span className="flex items-center justify-center w-3.5 h-3.5 text-primary-700">
            <i className="ri-check-double-line text-[13px]" aria-hidden="true" />
          </span>
          {detail}
        </p>
      ) : null}
      {footer !== undefined ? (
        footer
      ) : (
        <p className="mt-4 text-[11.5px] leading-relaxed text-foreground-400">{t('flow.successPending')}</p>
      )}
    </div>
  );
}